import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SliceState, Status } from "../types/common";
import { addCases, executePromise } from "../util/sliceUtil";
import { DataSearchApi } from "../api/DataSearchApi";
import { RootState } from "./index";
import { PageResponse } from "../types/page";
import {
  DatasetMeta,
  TestResultDataObj,
  TestResultItemDataObj,
  TestResultSearchCondition,
  TestResultWideDataObj,
} from "../types/dataSearch";

export interface DataSearchState extends SliceState {
  datasets?: DatasetMeta[];
  testResultList?: PageResponse<TestResultDataObj>;
  testResultItemList?: PageResponse<TestResultItemDataObj>;
  testResultWideList?: PageResponse<TestResultWideDataObj>;
}

const initialState: DataSearchState = {
  status: Status.IDLE,
};

export const getDatasetsAsync = createAsyncThunk("dataSearch/getDatasets", () =>
  executePromise(DataSearchApi.getDatasets())
);

export const getTestResultListAsync = createAsyncThunk(
  "dataSearch/getTestResultList",
  (condition: TestResultSearchCondition) => executePromise(DataSearchApi.getTestResultList(condition))
);

export const downloadTestResultAsync = createAsyncThunk(
  "dataSearch/downloadTestResult",
  (condition: TestResultSearchCondition) => executePromise(DataSearchApi.downloadTestResult(condition))
);

export const getTestResultItemListAsync = createAsyncThunk(
  "dataSearch/getTestResultItemList",
  (condition: TestResultSearchCondition) => executePromise(DataSearchApi.getTestResultItemList(condition))
);

export const downloadTestResultItemAsync = createAsyncThunk(
  "dataSearch/downloadTestResultItem",
  (condition: TestResultSearchCondition) => executePromise(DataSearchApi.downloadTestResultItem(condition))
);

export const getTestResultWideListAsync = createAsyncThunk(
  "dataSearch/getTestResultWideList",
  (condition: TestResultSearchCondition) => executePromise(DataSearchApi.getTestResultWideList(condition))
);

export const getTestResultWideColumnsAsync = createAsyncThunk(
  "dataSearch/getTestResultWideColumns",
  (condition: TestResultSearchCondition) => executePromise(DataSearchApi.getTestResultWideColumns(condition))
);

export const downloadTestResultWideAsync = createAsyncThunk(
  "dataSearch/downloadTestResultWide",
  (condition: TestResultSearchCondition) => executePromise(DataSearchApi.downloadTestResultWide(condition))
);

export const dataSearchSlice = createSlice({
  name: "dataSearch",
  initialState,
  reducers: {
    setDatasets: (state, action: PayloadAction<DatasetMeta[]>) => {
      state.datasets = action.payload;
    },
    setTestResultList: (state, action: PayloadAction<PageResponse<TestResultDataObj>>) => {
      state.testResultList = action.payload;
    },
    setTestResultItemList: (state, action: PayloadAction<PageResponse<TestResultItemDataObj>>) => {
      state.testResultItemList = action.payload;
    },
    setTestResultWideList: (state, action: PayloadAction<PageResponse<TestResultWideDataObj>>) => {
      state.testResultWideList = action.payload;
    },
  },
  extraReducers: (builder) => {
    addCases(builder, getDatasetsAsync);
    addCases(builder, getTestResultListAsync);
    addCases(builder, downloadTestResultAsync);
    addCases(builder, getTestResultItemListAsync);
    addCases(builder, downloadTestResultItemAsync);
    addCases(builder, getTestResultWideListAsync);
    addCases(builder, getTestResultWideColumnsAsync);
    addCases(builder, downloadTestResultWideAsync);
  },
});

export default dataSearchSlice.reducer;

export const { setDatasets, setTestResultList, setTestResultItemList, setTestResultWideList } =
  dataSearchSlice.actions;

export const dataSearchDatasets = (state: RootState) => state.dataSearch.datasets;
export const dataSearchTestResultList = (state: RootState) => state.dataSearch.testResultList;
export const dataSearchTestResultItemList = (state: RootState) => state.dataSearch.testResultItemList;
export const dataSearchTestResultWideList = (state: RootState) => state.dataSearch.testResultWideList;
export const dataSearchStatus = (state: RootState) => state.dataSearch.status;
